import { DialogRef } from '@angular/cdk/dialog';
import { Component, Inject, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SerialNumSQL } from '@interfaces/service-call';
import { AllProducts } from '@interfaces/SQL-morning-responses';
import { ServiceCallService } from '@services/service-call.service';
import { displayFn } from '@utils/display_functions/displayFn';
import { genericSearchingFunctionWithoutNullChecks } from '@utils/functions';
import { decompress } from 'lz-string';
import { map, Observable, startWith, Subject } from 'rxjs';

@Component({
	selector: 'app-teuda-service-call',
	templateUrl: './teuda-service-call.component.html',
	styleUrls: ['./teuda-service-call.component.scss'],
})
export class TeudaServiceCallComponent implements OnInit {
	private _serviceCall = inject(ServiceCallService);
	private fb = inject(FormBuilder);
	private dialogRef = inject(DialogRef);
	imagePrefix = '';

	title = 'פתיחת קריאת שירות';
	serialsLoading$ = this._serviceCall.loading$;
	serialNumbers$ = this._serviceCall.serialNumbers$.asObservable();
	products: AllProducts[];
	products$: Observable<AllProducts[]>;
	taskSendLoading$ = this._serviceCall.newServiceCallLoading$;
	taskID$ = this._serviceCall.serviceCallID$;
	uploadImageToServerCallback = (file: File) => this._serviceCall.uploadImageToServer(file);

	private _filter = genericSearchingFunctionWithoutNullChecks;
	form = this.fb.group({
		serialNumber: [<SerialNumSQL>null],
		product: [<AllProducts>null],
		remark: ['', [Validators.maxLength(68), Validators.required]],
		listType: [<'serials' | 'products'>'serials'],
	});

	constructor(@Inject(MAT_DIALOG_DATA) public data: { clientid: string }) {}

	ngOnInit(): void {
		this._serviceCall.init();
		this.imagePrefix = this._serviceCall.serviceCallID$.value;
		if (!this.data.clientid) {
			this.dialogRef.close();
			throw new Error('no client id');
		}
		this._serviceCall.handleGetSerialNumbers(this.data.clientid);

		if (localStorage.getItem('AllProducts')) {
			this.products = JSON.parse(decompress(localStorage.getItem('AllProducts')));
			this.products$ = this.form.controls.product.valueChanges.pipe(
				startWith(''),
				map(value => (value && typeof value === 'string' ? this._filter(value, this.products, ['ProductId', 'Product_Name']) : this.products.slice(0, 100))),
			);
		}

		this.serialNumbers$ = this.form.controls.serialNumber.valueChanges.pipe(
			startWith(''),
			map(value =>
				value && typeof value === 'string' ? this._filter(value, this._serviceCall.serialNumbers$.value, ['ProductId', 'SerNum']) : this._serviceCall.serialNumbers$.value.slice(0, 100),
			),
		);
	}

	addImageNameToUploadedImages(imageName: string) {
		this._serviceCall.addImageToUploadedImages(imageName);
	}

	getErrorMessage() {
		if (this.form.controls.remark.hasError('required')) {
			return 'שדה חובה';
		}
		if (this.form.controls.remark.hasError('maxlength')) {
			return 'לא ניתן לשלוח הערה עם יותר מ68 תווים';
		}

		return '';
	}

	onSubmit() {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		let { serialNumber, remark, product, listType } = this.form.value;

		switch (listType) {
			case 'products':
				serialNumber = { ClientId: '', SerNum: '', ProductId: product?.ProductId || '', ProductName: product?.Product_Name || '' };
				break;

			case 'serials':
				serialNumber = serialNumber ? serialNumber : { ClientId: '', SerNum: '', ProductId: '', ProductName: '' };
				break;

			default:
				break;
		}

		this._serviceCall.handleSubmitNewServiceCall({ serialNumber, remark });
	}

	backFromTaskError() {
		this._serviceCall.newServiceCallLoading$.next('idle');
	}

	displayFnSerials(value: SerialNumSQL) {
		return displayFn({ mode: 'serials', value });
	}
	displayFnProducts(value: AllProducts) {
		return displayFn({ mode: 'products', value });
	}
}
